// Helpers de Tienda Nube reusables desde el front:
//  - ensureTnProducts: trae la lista de productos de la tienda de una marca;
//    si la marca no está conectada, abre el popup de OAuth y reintenta.
//  - fireTnImport / fireTnExport: disparan la importación/exportación. Con
//    USE_ATLAS_BACKEND van al atlas (`/v1/products/tiendanube-import|export`,
//    JWT — user_id/id_company ya no viajan en el body); con el flag apagado,
//    a los webhooks de n8n byte-idénticos de siempre. Ver
//    ADR-2026-08-24-descripcion-y-tiendanube-al-atlas.
// El listado de productos y el OAuth siguen en las edge functions
// (tn-products/tn-connect/tn-product); el access_token nunca pasa por acá.

import { supabase } from "./supabase";
import { USE_ATLAS_BACKEND } from "../config";
import { atlasBackendFetch } from "./atlasBackend";
import { recortarAlTope } from "./limiteImagenesProducto";
import {
  CLAVE_AVISO_TN,
  claveDelAvisoTn,
  esDeEsteIntento,
  ESPERA_MAXIMA_TN_MS,
  leerAvisoDeTiendanube,
  MENSAJE_CONEXION_CANCELADA,
  MENSAJE_TIEMPO_AGOTADO,
  mensajeDeVentanaBloqueada,
  motivoDeCierre,
  type AvisoDeTiendanube,
} from "./tiendanubeConexionLogica";

const IMPORT_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/tiendanube-import";
const EXPORT_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/tiendanube-export";

export interface TnVariante {
  nombre: string;
  imgs: string[];
}

export interface TnProduct {
  id: number;
  nombre: string;
  /** Imagen principal (para la miniatura del grid). */
  img_src: string;
  /** Hasta 3 imágenes del producto (compat / caso sin colores). */
  imgs: string[];
  /** Variantes a crear (1..N). N>1 = producto con colores. */
  variantes: TnVariante[];
  /** Nombres de color para los circulitos del grid (opcional). */
  colores?: string[];
}

interface TnProductsResponse {
  connected: boolean;
  products?: TnProduct[];
  store_name?: string | null;
  /** true si la empresa YA tiene otra tienda conectada (para avisar al conectar una 2ª). */
  company_has_stores?: boolean;
}

async function fetchTnProducts(
  brandId: string | null,
): Promise<TnProductsResponse> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId },
  });
  if (error)
    throw new Error(error.message || "No se pudo traer la lista de productos.");
  return data as TnProductsResponse;
}

/**
 * Abre la ventana del OAuth. **Llamar SIEMPRE de forma SINCRÓNICA dentro del
 * handler del click** — nunca después de un `await` (ADR-2026-08-07-tiendanube-popup-safari).
 *
 * Motivo: los navegadores solo dejan abrir ventanas mientras dura la *transient
 * activation* que arranca con el gesto del usuario. Chrome y Firefox estiraron
 * ese permiso a ~5s (por eso el bug no se veía ahí), pero **WebKit lo corta
 * apenas la promesa cede el turno**: cualquier `await` en el medio y
 * `window.open` devuelve `null`. Como iOS obliga a todos los navegadores a usar
 * WebKit, en iPhone no funcionaba en ninguno.
 *
 * Se abre sobre una página propia de carga (mismo origen) y no sobre la URL de
 * autorización, porque esa URL todavía no la tenemos: hay que pedirla a la edge
 * function `tn-connect`. Una vez que llega, se navega la ventana con
 * `connectTnEnVentana`.
 */
export function abrirVentanaOAuth(): Window | null {
  return window.open("/tn-conectando.html", "tn_connect", "width=620,height=740");
}

/** Trae los productos de la tienda; si no está conectada, abre el popup de
 *  OAuth y reintenta. Devuelve los productos + el nombre de la tienda.
 *
 *  `opts.popup` es la ventana que el caller YA abrió en el click (ver
 *  `abrirVentanaOAuth`). Este flujo consulta los productos ANTES de saber si
 *  hace falta conectar, así que el caller la abre "por las dudas" y acá se
 *  **cierra sola** si la tienda ya estaba conectada.
 *
 *  `conecto: true` = la tienda se conectó en ESTA llamada (no estaba). Quien
 *  conecta desde Atlas trae el catálogo entero en ese caso
 *  (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo). */
export async function ensureTnProducts(
  brandId: string | null,
  opts?: { confirmNewStore?: () => Promise<boolean>; popup?: Window | null },
): Promise<{ products: TnProduct[]; storeName: string | null; conecto: boolean }> {
  const cerrarPopup = () => {
    try {
      opts?.popup?.close();
    } catch {
      /* noop */
    }
  };

  let res: TnProductsResponse;
  let conecto = false;
  try {
    res = await fetchTnProducts(brandId);
  } catch (e) {
    cerrarPopup(); // no dejar la ventana de carga colgada si falla la consulta
    throw e;
  }

  if (!res.connected) {
    // Si la empresa YA tiene otra tienda conectada (en otra marca), avisamos
    // antes de conectar una nueva: la que se conecta = la que el comerciante
    // tenga logueada en Tiendanube (no la podemos elegir nosotros — ver ADR-093).
    if (res.company_has_stores && opts?.confirmNewStore) {
      const ok = await opts.confirmNewStore();
      if (!ok) {
        cerrarPopup();
        throw new Error("CANCELLED");
      }
    }
    try {
      await connectTnEnVentana(brandId, opts?.popup);
    } catch (e) {
      cerrarPopup();
      throw e;
    }
    conecto = true;
    res = await fetchTnProducts(brandId);
  } else {
    cerrarPopup(); // ya estaba conectada: la ventana no hacía falta
  }

  if (!res.connected) throw new Error("No se pudo conectar la tienda.");
  return { products: res.products || [], storeName: res.store_name || null, conecto };
}

/**
 * Inicia el OAuth de Tiendanube sobre una ventana **ya abierta por el gesto del
 * usuario** (`abrirVentanaOAuth`), y espera la confirmación. NO trae productos.
 *
 * Si `popup` viene `null`/cerrada, el navegador bloqueó la ventana: se avisa con
 * un mensaje accionable en vez de fallar en silencio.
 */
export async function connectTnEnVentana(
  brandId: string | null,
  popup: Window | null | undefined,
): Promise<void> {
  if (!popup) {
    anotarEnElRegistroTn({ registrar: "ventana_bloqueada", brand_id: brandId });
    throw new Error(mensajeDeVentanaBloqueada(navigator.userAgent));
  }

  // Con tope: si el pedido no vuelve, la ventana de carga quedaba esperando para
  // siempre en "Conectando con Tiendanube…" (ADR-2026-10-07-la-conexion-con-tienda-nube-no-se-cuelga).
  const ESPERA_MAXIMA_MS = 20_000;
  const { data, error } = await Promise.race([
    supabase.functions.invoke("tn-connect", { body: { brand_id: brandId } }),
    new Promise<{ data: null; error: Error }>((resolve) =>
      setTimeout(
        () => resolve({ data: null, error: new Error("Tienda Nube tardó demasiado en responder. Probá de nuevo.") }),
        ESPERA_MAXIMA_MS,
      ),
    ),
  ]);
  const authorizeUrl = (data as { authorizeUrl?: string } | null)?.authorizeUrl;
  const intentoId = (data as { intentoId?: string } | null)?.intentoId;
  if (error || !authorizeUrl) {
    try {
      popup.close(); // no dejar la ventana de carga colgada
    } catch {
      /* noop */
    }
    throw new Error(
      (error as Error | null)?.message || "No se pudo iniciar la conexión.",
    );
  }

  try {
    // El usuario ya cerró la ventana de carga mientras pedíamos la URL.
    if (popup.closed) throw new Error(MENSAJE_CONEXION_CANCELADA);

    popup.location.href = authorizeUrl;
    await waitForConnect(popup, intentoId);
  } catch (e) {
    // Cerró la ventana o se agotó el tiempo: Tiendanube nunca volvió, y eso también queda en
    // el registro. Un error que vino de Tiendanube ya lo anotó tn-callback.
    const motivo = motivoDeCierre(e instanceof Error ? e.message : "");
    if (motivo && intentoId) anotarEnElRegistroTn({ cerrar_intento: intentoId, resultado: motivo });
    throw e;
  }
}

/**
 * Le cuenta a `tn-connect` cómo terminó un intento del lado del navegador (ventana bloqueada,
 * cerrada o vencida), para `tiendanube_conexion_intentos`. Sin esperar y sin fallar: el
 * registro nunca puede trabar ni cambiar la conexión.
 */
function anotarEnElRegistroTn(body: Record<string, unknown>): void {
  void supabase.functions.invoke("tn-connect", { body }).catch(() => {});
}

// NOTA: acá vivía `connectTn(brandId)`, que abría la ventana y hacía todo junto.
// Se eliminó a propósito: era una API que PARECÍA segura pero solo funcionaba si
// el caller la invocaba sin ningún `await` antes — justo el error que causó
// ADR-2026-08-07-tiendanube-popup-safari. El par `abrirVentanaOAuth()` (en el
// click) + `connectTnEnVentana(brandId, popup)` (después) deja el requisito a la
// vista y no se puede usar mal sin darse cuenta.

/** Dispara la importación (corre en background). Devuelve si el backend la
 *  ACEPTÓ, con el mismo contrato que `fireTnExport`: antes un 400/500 se perdía y
 *  quien llamaba contaba la tanda como mandada (review Greptile #154). */
export async function fireTnImport(params: {
  userId: string | undefined;
  companyId: string | null;
  brandId: string | null;
  productos: {
    nombre: string;
    imgs: string[];
    tn_product_id: number;
    variantes: { nombre: string; imgs: string[] }[];
  }[];
}): Promise<DisparoTn> {
  // Tope de imágenes por producto (MAX_IMAGENES_POR_PRODUCTO), igual que en la carga manual. La edge
  // function `tn-products` hoy devuelve hasta 3, así que en la práctica no
  // recorta nada — pero el importador deja de depender de ese número: si TN
  // empieza a devolver 8, entran 5 y no se llena la variante de fotos que la
  // IA nunca va a usar. Se aplica al producto y a cada variante (color).
  const productos = params.productos.map((p) => ({
    ...p,
    imgs: recortarAlTope(0, p.imgs).aceptadas,
    variantes: p.variantes.map((v) => ({
      ...v,
      imgs: recortarAlTope(0, v.imgs).aceptadas,
    })),
  }));
  try {
    if (USE_ATLAS_BACKEND) {
      // Atlas deriva user/company del JWT; brand_id sí viaja. Mismo contrato
      // fire-and-forget: el "listo" es el refetch de productos del caller.
      const res = await atlasBackendFetch("/v1/products/tiendanube-import", {
        brand_id: params.brandId,
        productos,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => null)) as { error?: string } | null;
        return { ok: false, motivo: cuerpo?.error || `http_${res.status}` };
      }
      return { ok: true };
    }
  } catch {
    return { ok: false, motivo: "sin_red" };
  }
  // Camino viejo (webhook, el rollback con VITE_USE_ATLAS_BACKEND="false"). Si la
  // respuesta se puede leer, manda: un error legible es un rechazo. Si el fetch TIRA, se
  // da por salido: CORS lo hace tirar aunque el pedido haya llegado y desde acá no se
  // distingue de un error de red; cortar ahí dejaría la tienda a medias por una
  // respuesta ilegible (review Greptile #154).
  try {
    const res = await fetch(IMPORT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: params.userId,
        id_company: params.companyId,
        brand_id: params.brandId,
        productos,
      }),
    });
    if (!res.ok) return { ok: false, motivo: `http_${res.status}` };
  } catch {
    /* ver arriba */
  }
  return { ok: true };
}

/** Un producto a exportar: su acción (agregar/reemplazar/crear) + sus fotos en orden. */
export interface TnExportProducto {
  producto_id: string; // UUID de Atlas (para vincular tn_product_id al crear)
  action: "add" | "replace" | "create";
  tn_product_id: number | null; // si add/replace; null si create
  // solo si create; stock/sku "" = omitir en TN
  nuevo?: {
    nombre: string;
    precio: string;
    descripcion: string;
    stock: string;
    sku: string;
  };
  /** Si viene de un lanzamiento: su fila, donde el worker anota cómo terminó
   *  (ADR-2026-09-30-publicar-lanzamiento-a-tiendanube-bugs, atlas-backend). */
  lanzamiento_producto_id?: string | null;
  /** Cada foto es GENERADA (`imagen_generada_id`) o SUBIDA a mano (`archivo`). */
  imagenes: { imagen_generada_id?: string; archivo?: string; src: string; orden: number }[];
}

/** Dispara la exportación en n8n (corre en background). Tolera que CORS bloquee
 *  la lectura de la respuesta: el request igual llega. Contrato per-producto. */
/**
 * El resultado del disparo. `ok: false` NO significa que la subida falló: significa que
 * el backend no la aceptó y no va a pasar nada. La diferencia importa para quien tiene
 * que contarle algo a una persona: decir "la estoy subiendo" cuando nadie la va a subir
 * es peor que decir que no se pudo.
 */
export interface DisparoTn {
  ok: boolean;
  /** El código del backend cuando lo hay, ej. `tienda_no_conectada`. */
  motivo?: string;
}

export async function fireTnExport(params: {
  userId?: string;
  companyId: string | null;
  brandId: string | null;
  productos: TnExportProducto[];
  /** Si es la publicación de un lanzamiento: el worker anota ahí cómo terminó cada producto. */
  lanzamientoId?: string | null;
}): Promise<DisparoTn> {
  try {
    if (USE_ATLAS_BACKEND) {
      // Atlas deriva user/company del JWT. Mismo contrato fire-and-forget: el
      // "listo" real sigue siendo el poll de `imagenes_generadas.tn_exported_at`
      // del caller (con el fix de que ya no hay falsos positivos: el atlas solo
      // marca exportada la imagen que TN aceptó de verdad).
      const res = await atlasBackendFetch("/v1/products/tiendanube-export", {
        brand_id: params.brandId,
        productos: params.productos,
        ...(params.lanzamientoId ? { lanzamiento_id: params.lanzamientoId } : {}),
      });
      if (!res.ok) {
        // El backend valida antes de encolar: sin tienda conectada contesta 400. Antes
        // esto se perdía y el caller creía que había salido.
        const cuerpo = (await res.json().catch(() => null)) as { error?: string } | null;
        return { ok: false, motivo: cuerpo?.error || `http_${res.status}` };
      }
      return { ok: true };
    }
    await fetch(EXPORT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: params.userId,
        id_company: params.companyId,
        brand_id: params.brandId,
        productos: params.productos,
      }),
    });
    // Camino viejo (webhook): CORS impide leer la respuesta, así que se da por salido.
    return { ok: true };
  } catch {
    return { ok: false, motivo: "sin_red" };
  }
}

/** Datos actuales de UN producto de Tiendanube (para mostrar read-only en Atlas). */
export interface TnProductDetails {
  nombre: string;
  descripcion: string;
  variante_count: number;
  variante_id: number | null;
  precio: string | null;
  stock: number | null;
  sku: string | null;
}

/** Trae los datos actuales de un producto de TN (GET vía edge function tn-product). */
export async function getTnProductDetails(
  brandId: string | null,
  tnProductId: number,
): Promise<TnProductDetails | null> {
  const { data, error } = await supabase.functions.invoke("tn-product", {
    body: { brand_id: brandId, tn_product_id: tnProductId },
  });
  if (error) return null;
  return data as TnProductDetails;
}

/** Guarda cambios de un producto de TN (PUT vía edge function tn-product). */
export async function updateTnProduct(params: {
  brandId: string | null;
  tnProductId: number;
  varianteId: number | null;
  editarVariante: boolean;
  nombre: string;
  descripcion: string;
  precio: string;
  stock: string;
  sku: string;
}): Promise<boolean> {
  const { error } = await supabase.functions.invoke("tn-product", {
    body: {
      action: "update",
      brand_id: params.brandId,
      tn_product_id: params.tnProductId,
      variante_id: params.varianteId,
      editar_variante: params.editarVariante,
      nombre: params.nombre,
      descripcion: params.descripcion,
      precio: params.precio,
      stock: params.stock,
      sku: params.sku,
    },
  });
  return !error;
}

/**
 * Los productos de la tienda que TODAVÍA NO están en Atlas.
 *
 * Es `tn-products` sin `all`, o sea CON el dedup: la edge descuenta los que ya se
 * importaron a esa marca, así que lo que vuelve es exactamente "lo que falta traer".
 *
 * A diferencia de `ensureTnProducts`, **no abre el OAuth** si la tienda no está
 * conectada: devuelve `connected: false` y listo. Eso la hace llamable desde donde no
 * hay un clic de la persona — una herramienta del agente, por ejemplo, donde abrir un
 * popup está prohibido por el navegador (ADR-2026-08-07-tiendanube-popup-safari).
 *
 * OJO: esto SÍ lee el catálogo en la API de Tiendanube. Llamarla sólo cuando la persona
 * lo pidió, nunca de fondo ni al abrir una pantalla.
 */
export async function listTnProductsPendientes(brandId: string | null): Promise<{
  connected: boolean;
  storeName: string | null;
  productos: TnProduct[];
}> {
  const res = await fetchTnProducts(brandId);
  return {
    connected: !!res.connected,
    storeName: res.store_name || null,
    productos: res.products || [],
  };
}

/** Trae TODOS los productos de la tienda (sin dedup) — para el picker "agregar a
 *  otro producto". Tira error si la invocación falla, para que el picker pueda
 *  distinguir "error de red/servidor" de "tienda vacía" (y ofrecer reintentar). */
export async function listTnProductsAll(
  brandId: string | null,
): Promise<TnProduct[]> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId, all: true },
  });
  if (error)
    throw new Error(
      error.message || "No se pudieron traer los productos de tu tienda.",
    );
  const d = data as { products?: TnProduct[] };
  return d?.products || [];
}

/** Estado de conexión de Tienda Nube de una marca (liviano: NO trae productos).
 *
 *  `error: true` = la edge function NO respondió, así que `connected: false` es
 *  "no sabemos", no "no está conectada". El campo es aditivo y por defecto nadie
 *  lo mira (los callers que sólo quieren un booleano siguen andando igual); lo
 *  usa la pantalla de datos del Estudio de marketing, que tiene que distinguir
 *  "sin conectar" de "falló la consulta" en vez de mandar al usuario a
 *  reconectar una tienda que ya estaba conectada. */
export async function getTnStatus(
  brandId: string | null,
): Promise<{
  connected: boolean;
  storeName: string | null;
  companyHasStores: boolean;
  error: boolean;
}> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId, statusOnly: true },
  });
  if (error)
    return { connected: false, storeName: null, companyHasStores: false, error: true };
  const d = data as {
    connected?: boolean;
    store_name?: string | null;
    company_has_stores?: boolean;
  };
  return {
    connected: !!d?.connected,
    storeName: d?.store_name || null,
    companyHasStores: !!d?.company_has_stores,
    error: false,
  };
}

/** Desconecta la Tienda Nube de una marca (borra la integración). */
export async function disconnectTn(brandId: string | null): Promise<void> {
  const { error } = await supabase.functions.invoke("tn-disconnect", {
    body: { brand_id: brandId },
  });
  if (error) throw new Error(error.message || "No se pudo desconectar.");
}

/**
 * Borra el aviso de este intento y, de paso, los avisos vencidos de intentos que nadie levantó
 * (una pestaña que se cerró antes). Los de otras pestañas que siguen esperando quedan.
 */
function olvidarAvisoTn(intentoId: string | undefined): void {
  try {
    localStorage.removeItem(claveDelAvisoTn(intentoId));
    const viejas: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CLAVE_AVISO_TN) && !leerAvisoDeTiendanube(localStorage.getItem(k))) viejas.push(k);
    }
    viejas.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* noop */
  }
}

/** El aviso guardado de este intento (no el de otra pestaña ni uno viejo). */
function avisoTnGuardado(intentoId: string | undefined): AvisoDeTiendanube | null {
  try {
    const aviso = leerAvisoDeTiendanube(localStorage.getItem(claveDelAvisoTn(intentoId)));
    return aviso && esDeEsteIntento(aviso, intentoId, true) ? aviso : null;
  } catch {
    return null;
  }
}

// Resuelve cuando el popup confirma ('tn-connected' desde /tn-connected.html,
// mismo origen); rechaza si lo cierran antes o si se agota el tiempo.
// El aviso llega por postMessage (lo normal) o por localStorage: en los celulares
// la pestaña de Tiendanube pierde el `opener` y no le puede hablar a Atlas, así que
// la página lo deja guardado y acá se levanta por el evento `storage`, al volver a
// la pestaña o al ver la ventana cerrada (ADR-2026-10-06-tiendanube-la-ventana-que-no-vuelve).
// Del localStorage sólo vale el aviso de ESTE intento: si otra pestaña de Atlas está
// conectando a la vez, su aviso no termina esta espera (review Greptile #162).
function waitForConnect(popup: Window, intentoId: string | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;

    const cleanup = () => {
      done = true;
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVolver);
      window.removeEventListener("focus", onVolver);
      clearInterval(poll);
      clearTimeout(timeout);
      olvidarAvisoTn(intentoId);
      try {
        popup.close();
      } catch {
        /* noop */
      }
    };

    const terminar = (aviso: AvisoDeTiendanube) => {
      if (done) return;
      cleanup();
      if ("error" in aviso) reject(new Error(aviso.error));
      else resolve();
    };

    const onMessage = (e: MessageEvent) => {
      if (e.source !== popup && e.origin !== window.location.origin) return;
      const aviso = leerAvisoDeTiendanube(e.data);
      if (aviso && esDeEsteIntento(aviso, intentoId, false)) terminar(aviso);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== claveDelAvisoTn(intentoId)) return;
      const aviso = leerAvisoDeTiendanube(e.newValue);
      if (aviso && esDeEsteIntento(aviso, intentoId, true)) terminar(aviso);
    };

    // Al volver a la pestaña de Atlas (el celular suele congelarla mientras está atrás).
    const onVolver = () => {
      if (document.visibilityState === "hidden") return;
      const aviso = avisoTnGuardado(intentoId);
      if (aviso) terminar(aviso);
    };

    const poll = setInterval(() => {
      if (!popup.closed || done) return;
      // Puede que la página ya haya dejado el aviso y la ventana se cerró antes del evento.
      const aviso = avisoTnGuardado(intentoId);
      if (aviso) terminar(aviso);
      else {
        cleanup();
        reject(new Error(MENSAJE_CONEXION_CANCELADA));
      }
    }, 600);

    const timeout = setTimeout(() => {
      if (!done) {
        cleanup();
        reject(new Error(MENSAJE_TIEMPO_AGOTADO));
      }
    }, ESPERA_MAXIMA_TN_MS);

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVolver);
    window.addEventListener("focus", onVolver);
  });
}
